import React, { useEffect, useState } from "react";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import { Package, Clock, CheckCircle2, Truck } from "lucide-react";

const STAGES = [
    { key: "pending", label: "Placed", icon: Clock },
    { key: "confirmed", label: "Confirmed", icon: CheckCircle2 },
    { key: "shipped", label: "Shipped", icon: Truck },
    { key: "delivered", label: "Delivered", icon: Package },
];

function stageIndex(status) {
    const map = { pending: 0, confirmed: 1, shipped: 2, delivered: 3 };
    return map[status] ?? 0;
}

export default function OrderTracking() {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const { data } = await api.get("/orders");
                setOrders(data);
            } finally {
                setLoading(false);
            }
        })();
    }, [user]);

    const filtered = search ? orders.filter((o) => o.id.includes(search)) : orders;

    return (
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 animate-fade-slide">
            <h1 className="font-heading font-black text-4xl md:text-6xl tracking-tighter mb-6">Track orders</h1>
            <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by order ID"
                data-testid="track-search"
                className="w-full md:max-w-md bg-white border border-brand-line rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-orange mb-8"
            />
            {loading ? (
                <p className="text-brand-moss">Loading...</p>
            ) : filtered.length === 0 ? (
                <div className="bg-white border border-brand-line rounded-3xl p-12 text-center">
                    <Package size={48} className="mx-auto text-brand-moss mb-4" />
                    <p className="text-brand-moss">No orders found.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {filtered.map((order) => {
                        const idx = stageIndex(order.status);
                        return (
                            <div key={order.id} data-testid={`order-row-${order.id}`} className="bg-white border border-brand-line rounded-2xl p-6">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5">
                                    <div>
                                        <div className="text-xs font-bold uppercase tracking-wider text-brand-moss">Order ID</div>
                                        <div className="font-mono text-sm">{order.id}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold uppercase tracking-wider text-brand-moss">Total</div>
                                        <div className="font-heading font-black text-xl">₹{order.totals?.total?.toFixed(2)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold uppercase tracking-wider text-brand-moss">Placed</div>
                                        <div className="text-sm">{new Date(order.created_at).toLocaleDateString()}</div>
                                    </div>
                                    <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider ${
                                        order.payment_status === "paid" ? "bg-brand-sage/10 text-brand-sage" : "bg-brand-sand text-brand-ink"
                                    }`}>
                                        {order.payment_status}
                                    </span>
                                </div>
                                <div className="grid grid-cols-4 gap-2">
                                    {STAGES.map((s, i) => {
                                        const Icon = s.icon;
                                        const active = i <= idx;
                                        return (
                                            <div key={s.key} className="flex flex-col items-center text-center relative">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${active ? "bg-brand-orange text-white" : "bg-brand-cream text-brand-moss"}`}>
                                                    <Icon size={16} />
                                                </div>
                                                <span className={`text-xs font-bold mt-2 ${active ? "text-brand-ink" : "text-brand-moss"}`}>
                                                    {s.label}
                                                </span>
                                                {i < STAGES.length - 1 && (
                                                    <div className={`absolute top-5 left-1/2 w-full h-0.5 ${i < idx ? "bg-brand-orange" : "bg-brand-line"}`} style={{ zIndex: -1 }} />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
